(() => {
  if (window.tnColorVisibility) return;
  const cache = new Map();
  const queue = [];
  let running = 0;
  const maxParallel = 3;
  const processed = new WeakSet();
  const revisions = new WeakMap();

  function display(element, visible) {
    if (!element) return;
    element.hidden = !visible;
    if (visible) element.style.removeProperty('display');
    else element.style.setProperty('display', 'none', 'important');
  }

  async function inspectPage(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    let reader;
    try {
      const response = await fetch(url, {
        credentials: 'omit',
        cache: 'no-store',
        signal: controller.signal
      });
      if (!response.ok) return false;
      const finalURL = new URL(response.url || url, location.origin);
      if (finalURL.origin !== location.origin || !finalURL.pathname.includes('/products/')) return false;
      let html = '';
      if (response.body?.getReader) {
        reader = response.body.getReader();
        const decoder = new TextDecoder();
        let bytes = 0;
        while (true) {
          const chunk = await reader.read();
          if (chunk.done) break;
          bytes += chunk.value.byteLength;
          html += decoder.decode(chunk.value, { stream: true });
          if (/<\/head\s*>/i.test(html)) break;
          if (bytes > 524288) return false;
        }
      } else html = await response.text();
      const end = html.search(/<\/head\s*>/i);
      if (end < 0) return false;
      const head = new DOMParser().parseFromString(html.slice(0, end) + '</head></html>', 'text/html');
      const robots = Array.from(head.querySelectorAll('meta[name]'))
        .filter(meta => meta.name.toLowerCase() === 'robots')
        .flatMap(meta => meta.content.toLowerCase().split(/[\s,]+/));
      const headerRobots = (response.headers?.get('x-robots-tag') || '').toLowerCase();
      if (robots.includes('noindex') || robots.includes('none') || /\b(noindex|none)\b/.test(headerRobots)) return false;
      return Boolean(head.querySelector('meta[property="og:type"][content="product"]'));
    } catch (_) {
      return false;
    } finally {
      clearTimeout(timer);
      if (reader) {
        try { await reader.cancel(); } catch (_) {}
      }
    }
  }

  function pump() {
    while (running < maxParallel && queue.length) {
      const job = queue.shift();
      running += 1;
      inspectPage(job.url).then(job.resolve).finally(() => {
        running -= 1;
        pump();
      });
    }
  }

  function listed(href) {
    const url = new URL(href, location.origin);
    if (url.origin !== location.origin) return Promise.resolve(false);
    url.search = '';
    url.hash = '';
    const key = url.href;
    if (!cache.has(key)) {
      cache.set(key, new Promise(resolve => {
        queue.push({ url: key, resolve });
        pump();
      }));
    }
    return cache.get(key);
  }

  async function update(group) {
    const revision = (revisions.get(group) || 0) + 1;
    revisions.set(group, revision);
    const links = Array.from(group.querySelectorAll('[data-tn-color-target]'));
    const wrapper = group.closest('[data-tn-color-wrapper]');
    const nativeCount = Number(wrapper?.dataset.tnNativeColors || 0);
    display(group, false);
    if (wrapper && nativeCount === 1) display(wrapper, false);
    const visible = await Promise.all(links.map(link => listed(link.href)));
    if (!group.isConnected || revisions.get(group) !== revision) return;
    const valid = links.filter((_, index) => visible[index]);
    const grid = group.dataset.tnColorGroup === 'grid';
    const collapsed = grid && valid.length >= Number(group.dataset.tnCollapseFrom || 3);
    links.forEach((link, index) => {
      const element = grid ? link.closest('.cg-grid__obj') || link : link;
      display(element, visible[index] && (!collapsed || link === valid[0]));
    });
    const more = group.querySelector('[data-tn-color-more]');
    if (more) {
      more.textContent = collapsed ? '+' + (valid.length - 1) : '';
      display(more, collapsed);
    }
    display(group, valid.length > 0);
    if (wrapper) {
      const otherCount = valid.filter(link => link.dataset.tnColorOwn !== 'true').length;
      display(wrapper, nativeCount > 1 || (nativeCount === 1 && otherCount > 0));
    }
    group.dataset.tnColorChecked = 'true';
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      observer.unobserve(entry.target);
      entry.target.querySelectorAll('[data-tn-color-group="grid"]').forEach(update);
    }
  }, { rootMargin: '200px' }) : null;

  function scan() {
    document.querySelectorAll('[data-tn-color-group]').forEach(group => {
      if (processed.has(group)) return;
      processed.add(group);
      const card = group.closest('.grid-product');
      if (observer && group.dataset.tnColorGroup === 'grid' && card) observer.observe(card);
      else update(group);
    });
  }

  window.tnColorVisibility = { refresh() {
    cache.clear();
    document.querySelectorAll('[data-tn-color-group]').forEach(update);
  } };
  let pending = false;
  new MutationObserver(records => {
    if (pending || !records.some(record => Array.from(record.addedNodes).some(node =>
      node.nodeType === 1 && (node.matches('[data-tn-color-group]') || node.querySelector('[data-tn-color-group]'))
    ))) return;
    pending = true;
    queueMicrotask(() => { pending = false; scan(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('pageshow', event => {
    if (event.persisted) window.tnColorVisibility.refresh();
  });
  document.addEventListener('shopify:section:load', scan);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
})();
